// src/utils/gameCatalog.ts
import API_BASE_URL from './apiBase';

export type GameAccessDuration = 'day' | 'week' | 'month';

export type GameStatus = 'planned' | 'designing' | 'in-development' | 'beta' | 'active' | 'inactive';

export const GAME_STATUS_LABELS: Record<GameStatus, string> = {
  planned: 'Planned',
  designing: 'Designing',
  'in-development': 'In Development',
  beta: 'Beta',
  active: 'Active',
  inactive: 'Inactive',
};

export const VISIBLE_STATUSES: GameStatus[] = ['beta', 'active'];

// Valid credit billing periods — mirrors appCatalog.CreditPeriod so the
// admin UI can use the same constants regardless of target type. Kept
// in lockstep with migration 014 which widened the enum to include
// annual billing.
export type CreditPeriod = 'day' | 'week' | 'month' | 'year';

export const CREDIT_PERIOD_LABELS: Record<CreditPeriod, string> = {
  day: 'Per Day',
  week: 'Per Week',
  month: 'Per Month',
  year: 'Per Year',
};

export interface GameInfo {
  id: string;
  name: string;
  description: string;
  image?: string;
  launchUrl: string;
  status?: GameStatus;
  creditCost: number;
  creditPeriod: CreditPeriod;
}

export interface GamePassProduct {
  id: number;
  type: 'pass';
  gameId: string;
  duration: GameAccessDuration;
  name: string;
  description: string;
  price: number;
}

export interface GamePackProduct {
  id: number;
  type: 'pack';
  gameIds: string[];
  duration: GameAccessDuration;
  name: string;
  description: string;
  price: number;
}

export type GameProduct = GamePassProduct | GamePackProduct;

export const DURATION_LABELS: Record<GameAccessDuration, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
};

// API functions for games catalog
export const fetchGamesCatalog = async (token?: string | null): Promise<GameInfo[]> => {
  try {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/games/catalog`, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch games catalog');
    }

    const data = await response.json();
    return data.map((game: {
      id: string; name: string; description: string;
      image_url?: string; launch_url: string; status: GameStatus;
      credit_cost?: number; credit_period?: CreditPeriod;
    }) => ({
      id: game.id,
      name: game.name,
      description: game.description,
      image: game.image_url,
      launchUrl: game.launch_url,
      status: game.status,
      creditCost: Number(game.credit_cost ?? 0),
      creditPeriod: (game.credit_period as CreditPeriod) ?? 'month',
    }));
  } catch (error) {
    console.error('Failed to fetch games catalog:', error);
    return [];
  }
};

// API functions for game statuses (admin only)
export const fetchGameStatuses = async (token: string): Promise<Record<string, GameStatus>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/statuses`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch game statuses');
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch game statuses:', error);
    return {};
  }
};

const mapGameRow = (row: {
  id: string; name: string; description?: string;
  image_url?: string; launch_url: string; status: GameStatus;
  credit_cost?: number; credit_period?: CreditPeriod;
}): GameInfo => ({
  id: row.id,
  name: row.name,
  description: row.description || '',
  image: row.image_url,
  launchUrl: row.launch_url,
  status: row.status,
  creditCost: Number(row.credit_cost ?? 0),
  creditPeriod: (row.credit_period as CreditPeriod) ?? 'month',
});

export const createGame = async (
  token: string,
  game: {
    name: string;
    launchUrl: string;
    status: GameStatus;
    description?: string;
    creditCost?: number;
    creditPeriod?: CreditPeriod;
  }
): Promise<GameInfo | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/catalog`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(game),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to create game');
    }
    return mapGameRow(await response.json());
  } catch (error) {
    console.error('Failed to create game:', error);
    throw error;
  }
};

/**
 * Update a game's credit cost + period. Dedicated PATCH so admins can
 * tweak pricing without touching name/URL/status.
 */
export const updateGameCredits = async (
  token: string,
  gameId: string,
  data: { creditCost: number; creditPeriod: CreditPeriod },
): Promise<GameInfo | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/${gameId}/credits`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) return null;
    return mapGameRow(await response.json());
  } catch (error) {
    console.error('Failed to update game credits:', error);
    return null;
  }
};

export const updateGameStatus = async (token: string, gameId: string, status: GameStatus): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/${gameId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
    return response.ok;
  } catch (error) {
    console.error('Failed to update game status:', error);
    return false;
  }
};

// API functions for game products
export const fetchGameProducts = async (token?: string | null): Promise<GameProduct[]> => {
  try {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/games/products`, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch game products');
    }

    const data = await response.json();
    return data.map((product: { id: number; type: string; name: string; description: string; price: number; duration: string; gameId?: string; gameIds?: string[] }) => {
      if (product.type === 'pass') {
        return {
          id: product.id,
          type: 'pass' as const,
          name: product.name,
          description: product.description,
          price: product.price,
          duration: product.duration as GameAccessDuration,
          gameId: product.gameId!,
        };
      } else {
        return {
          id: product.id,
          type: 'pack' as const,
          name: product.name,
          description: product.description,
          price: product.price,
          duration: product.duration as GameAccessDuration,
          gameIds: product.gameIds || [],
        };
      }
    });
  } catch (error) {
    console.error('Failed to fetch game products:', error);
    return [];
  }
};

export const createGameProduct = async (
  token: string,
  product: Omit<GamePassProduct, 'id'> | Omit<GamePackProduct, 'id'>
): Promise<GameProduct | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(product),
    });
    if (!response.ok) {
      throw new Error('Failed to create game product');
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to create game product:', error);
    return null;
  }
};

export const deleteGameProduct = async (token: string, productId: number): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/products/${productId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.ok;
  } catch (error) {
    console.error('Failed to delete game product:', error);
    return false;
  }
};

export const resetGameProducts = async (token: string): Promise<GameProduct[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/games/products/reset`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error('Failed to reset game products');
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to reset game products:', error);
    return [];
  }
};
